import { useEffect, useRef, useState, type ReactNode } from "react";

import { useReducedMotion } from "../hooks/useReducedMotion.js";

interface RevealProps {
  children: ReactNode;
  /** Stagger, in milliseconds, for items revealed as a group. */
  delay?: number;
  className?: string;
}

/**
 * Fades and lifts its children in the first time they scroll into view.
 *
 * The observer disconnects on the first intersection: content that has been
 * read once should not animate again when the reader scrolls back up, which is
 * the thing that makes scroll animation feel cheap.
 *
 * Under reduced motion this renders visible immediately and never observes
 * anything — importantly it does *not* just skip the transition, since content
 * left at `opacity: 0` would be invisible rather than merely un-animated.
 */
export function Reveal({ children, delay = 0, className = "" }: RevealProps) {
  const host = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (reduced) {
      setShown(true);
      return;
    }

    const element = host.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setShown(true);
        observer.disconnect();
      },
      { rootMargin: "0px 0px -8% 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [reduced]);

  return (
    <div
      ref={host}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-[opacity,transform] duration-700 ease-out ${
        shown ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
      } ${className}`}
    >
      {children}
    </div>
  );
}
